import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const DIMENSIONS = [
  ["query", "Search terms"],
  ["page", "Landing pages"],
  ["date", "By day"],
  ["device", "Device"],
  ["country", "Country"],
];

const pct = (n) => `${(n * 100).toFixed(1)}%`;

const Head = ({ children, right }) => (
  <th
    className={`text-[10px] tracking-[0.16em] uppercase text-[#9A968E] font-normal pb-2 ${
      right ? "text-right" : "text-left"
    }`}
  >
    {children}
  </th>
);

/**
 * Real Google Search Console figures, or an honest "not connected" panel. This component never
 * falls back to an estimate: if Google has not answered, it says so.
 */
export const SearchPerformance = ({ setupSteps }) => {
  const [dimension, setDimension] = useState("query");
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    api
      .get("/admin/search-console/performance", { params: { dimension, days } })
      .then((r) => live && setData(r.data))
      .catch((err) => live && setData({ connected: false, reason: "request_failed",
        detail: err.response?.data?.detail || err.message }));
    return () => {
      live = false;
    };
  }, [dimension, days]);

  if (data === null)
    return (
      <p className="text-[13px] text-[#6E6A63]" data-testid="gsc-loading">
        Asking Google for your figures…
      </p>
    );

  if (!data.connected)
    return (
      <div className="bg-white border border-[#E6E3DC] p-5" data-testid="gsc-not-connected">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-1 text-[#B4762F] shrink-0" />
          <div>
            <h3 className="display text-[17px] text-charcoal">
              Search Console is verified — not yet readable here
            </h3>
            <p className="text-[13px] text-[#6E6A63] mt-2 leading-relaxed">{data.detail}</p>
            <p className="text-[13px] text-[#454545] mt-3">
              Property: <span className="text-[#6E6A63]">{data.property}</span>
            </p>
            {setupSteps && (
              <ol className="mt-4 space-y-1.5">
                {setupSteps.map((s, i) => (
                  <li key={i} className="text-[13px] text-[#454545] flex gap-2">
                    <span className="text-sage">{i + 1}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    );

  const totals = data.rows.reduce(
    (a, r) => ({ clicks: a.clicks + r.clicks, impressions: a.impressions + r.impressions }),
    { clicks: 0, impressions: 0 }
  );

  return (
    <div data-testid="gsc-connected">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 bg-sage text-white px-2.5 py-1 text-[10px] tracking-[0.12em] uppercase">
          <CheckCircle2 className="w-3 h-3" />
          Google Search Console — reported data
        </span>
        <span className="text-[12px] text-[#9A968E]">Final data through {data.data_through}</span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {DIMENSIONS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setDimension(key)}
            data-testid={`gsc-dimension-${key}`}
            className={`px-3 py-1.5 text-[12px] transition-colors ${
              dimension === key
                ? "bg-charcoal text-white"
                : "bg-white border border-[#E6E3DC] text-[#6E6A63] hover:text-charcoal"
            }`}
          >
            {label}
          </button>
        ))}
        {[28, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`gsc-days-${d}`}
            className={`px-3 py-1.5 text-[12px] transition-colors ${
              days === d
                ? "bg-sage text-white"
                : "bg-white border border-[#E6E3DC] text-[#6E6A63] hover:text-charcoal"
            }`}
          >
            {d} days
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white border border-[#E6E3DC] p-4">
          <div className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">Clicks</div>
          <div className="display text-[24px] text-charcoal mt-1">{totals.clicks}</div>
        </div>
        <div className="bg-white border border-[#E6E3DC] p-4">
          <div className="text-[10px] tracking-[0.16em] uppercase text-[#9A968E]">Impressions</div>
          <div className="display text-[24px] text-charcoal mt-1">{totals.impressions}</div>
        </div>
      </div>

      {data.rows.length === 0 ? (
        <p className="text-[13px] text-[#6E6A63] leading-relaxed" data-testid="gsc-empty">
          {dimension === "query"
            ? "Google returned no search terms for this window. It withholds rare queries to "
              + "protect privacy, so on a site with only a handful of impressions the terms stay "
              + "hidden even though the impressions are real. They appear as volume grows."
            : "Google returned no rows for this window. On a newly indexed site that usually means "
              + "impressions have not started yet, not that something is broken."}
        </p>
      ) : (
        <div className="bg-white border border-[#E6E3DC] overflow-x-auto">
          <table className="w-full min-w-[520px] text-[13px]">
            <thead>
              <tr className="border-b border-[#EDEAE3]">
                <th className="text-left text-[10px] tracking-[0.16em] uppercase text-[#9A968E] font-normal p-4">
                  {DIMENSIONS.find(([k]) => k === dimension)[1]}
                </th>
                <Head right>Clicks</Head>
                <Head right>Impr.</Head>
                <Head right>CTR</Head>
                <Head right>Pos.</Head>
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key} className="border-b border-[#F3F1EC] last:border-0">
                  <td className="p-4 text-[#454545] break-all">{r.key}</td>
                  <td className="text-right text-charcoal">{r.clicks}</td>
                  <td className="text-right text-[#6E6A63]">{r.impressions}</td>
                  <td className="text-right text-[#6E6A63]">{pct(r.ctr)}</td>
                  <td className="text-right text-[#6E6A63]">{r.position.toFixed(1)}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[12px] text-[#9A968E] mt-4 leading-relaxed">
        Straight from Search Console, read-only. Google finalises a day's figures two to three days
        later, so the most recent days are not missing — they are not final yet. Average position is
        an aggregate, not a fixed rank.
      </p>
    </div>
  );
};
